/**
 * Prix d'une classe de véhicule avec chauffeur (berline, van, Classe S, SUV,
 * minibus), en trois langues.
 *
 * MESURE 2026-10-09 : les cinq pages véhicule répondaient « Comment obtenir
 * un tarif ? » par « devis personnalisé », sans un seul montant, alors que la
 * grille publique du site (DisposalPricingTable, pages transfert) les affiche.
 * Tout est lu dans VEHICLES (app/config/infos.ts) : un forfait absent fait
 * échouer le build.
 *
 * Le véhicule blindé (CLASS_SECURITY) n'est volontairement PAS couvert : ses
 * prix sont absents des tableaux publics, la page sécurité reste sur devis.
 */
import { VEHICLES, getRouteKey, type VehicleId } from "@/config/infos";

type Locale = "fr" | "en" | "ar";
type PublicClass = Exclude<VehicleId, "CLASS_SECURITY">;

const pricesFor = (id: PublicClass) => {
  const v = VEHICLES[id];
  const route = (from: string, to: string) => {
    const price = v.fixedTransfers[getRouteKey(from, to)];
    if (typeof price !== "number")
      throw new Error(`vehiclePricing: pas de forfait ${from}-${to} en ${id}`);
    return price;
  };
  const pkg = (hours: number) => {
    const p = v.disposalPackages.find((d) => d.hours === hours);
    if (!p)
      throw new Error(`vehiclePricing: pas de forfait ${hours} h en ${id}`);
    return p;
  };
  const fixed = Object.values(v.fixedTransfers);
  if (!fixed.length) throw new Error(`vehiclePricing: aucun forfait en ${id}`);
  return {
    min: Math.min(...fixed),
    nice: route("CANNES", "NCE_AIRPORT"),
    monaco: route("MONACO", "NCE_AIRPORT"),
    cannesMonaco: route("CANNES", "MONACO"),
    h3: pkg(3).price,
    h8: pkg(8).price,
    extra: pkg(3).extraHour,
    km: v.pricePerKm,
  };
};

const km = (n: number, locale: Locale) =>
  locale === "fr" ? n.toFixed(2).replace(".", ",") : n.toFixed(2);

/**
 * `what` : le sujet de la question, déjà accordé
 * (« d'une berline avec chauffeur », « a sedan with chauffeur »,
 * « سيدان مع سائق »).
 */
export const vehiclePriceFaq = (
  locale: Locale,
  id: PublicClass,
  what: string,
): { q: string; a: string } => {
  const p = pricesFor(id);
  if (locale === "en")
    return {
      q: `How much does ${what} cost?`,
      a: `Common journeys are fixed prices: Nice airport ⇄ Cannes €${p.nice}, Nice airport ⇄ Monaco €${p.monaco}, Cannes ⇄ Monaco €${p.cannesMonaco}. Other journeys are estimated at €${km(p.km, "en")} per km. To keep the chauffeur, hourly hire costs €${p.h3} for 3 hours and €${p.h8} for an 8-hour day, then €${p.extra} per extra hour. These are our public rates, and the final price is confirmed in writing before the service.`,
    };
  if (locale === "ar")
    return {
      q: `كم سعر ${what}؟`,
      a: `الرحلات الشائعة بسعر ثابت: مطار نيس ⇄ كان ${p.nice} يورو، مطار نيس ⇄ موناكو ${p.monaco} يورو، كان ⇄ موناكو ${p.cannesMonaco} يورو. وتُقدَّر الرحلات الأخرى بـ${km(p.km, "ar")} يورو للكيلومتر. ولإبقاء السائق، تكلّف الخدمة بالساعة ${p.h3} يورو لثلاث ساعات و${p.h8} يورو ليوم من 8 ساعات، ثم ${p.extra} يورو لكل ساعة إضافية. هذه أسعارنا المعلنة، ويُؤكَّد السعر النهائي كتابيًا قبل الخدمة.`,
    };
  return {
    q: `Quel est le prix ${what} ?`,
    a: `Les trajets courants sont à prix fixe : aéroport de Nice ⇄ Cannes ${p.nice} €, aéroport de Nice ⇄ Monaco ${p.monaco} €, Cannes ⇄ Monaco ${p.cannesMonaco} €. Les autres trajets sont estimés à ${km(p.km, "fr")} € du kilomètre. Pour garder le chauffeur, la mise à disposition coûte ${p.h3} € les 3 h et ${p.h8} € la journée de 8 h, puis ${p.extra} € l'heure supplémentaire. Ce sont nos tarifs publics, et le prix final est confirmé par écrit avant la prestation.`,
  };
};

/** Titre et description chiffrés (FR et EN ; l'arabe garde les siens). */
export const vehicleSeo = (
  locale: "fr" | "en",
  id: PublicClass,
  title: string,
  lead: string,
): { title: string; description: string } => {
  const p = pricesFor(id);
  return locale === "en"
    ? {
        title: `${title} from €${p.min}`,
        description: `${lead}: Nice airport ⇄ Cannes €${p.nice}, ⇄ Monaco €${p.monaco}, hourly hire from €${p.h3} for 3 hours. Fixed prices, 24/7.`,
      }
    : {
        title: `${title} dès ${p.min} €`,
        description: `${lead} : aéroport de Nice ⇄ Cannes ${p.nice} €, ⇄ Monaco ${p.monaco} €, mise à disposition dès ${p.h3} € les 3 h. Prix fixes, 24 h/24.`,
      };
};
